import { MainButton } from "../../components/MainButton";
import { useFormik } from "formik";
import * as yup from "yup";
import { TextField } from "@mui/material";
import { HashLoader } from "react-spinners";
import { AiOutlineSave } from "react-icons/ai";
import Chip from "@mui/material/Chip";
import { MdOutlineFileCopy } from "react-icons/md";
import { BsCheckCircleFill } from "react-icons/bs";
import { useDispatch } from "react-redux";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";

import { useState } from "react";
import { COLORS } from "../../utils/colors";
import { updateOrg } from "../../api/user";
import { updateUser } from "../../store/mainReducer";

const MenuProps = {
  PaperProps: {
    style: {
      fontFamily: "AuthenticSans90",
    },
  },
};

const validationSchema = yup.object({
  email: yup
    .string("Enter your email")
    .email("Enter a valid email")
    .required("Email required"),
  website: yup
    .string("Enter the website")
    .url("Please enter a valid URL in format https://example.com")
    .required("Company website required"),
  company_name: yup
    .string("Enter the company name")
    .min(3, "The company name should be of minimum 3 characters length")
    .required("Company name required"),
  contact_person: yup
    .string("Enter the contact person")
    .min(4, "Name too short, minimum 4 characters required")
    .required("Contact person required"),
  support_email: yup
    .string("Enter the support email")
    .email("Enter a valid email"),
});

const fields1 = [
  {
    name: "Company name",
    id: "company_name",
    description:
      "The name of your organization will be shown in the confirmation emails and will be used in other communication channels with the ticket buyers",
  },

  {
    name: "Contact person",
    id: "contact_person",
  },
  {
    name: "Support Email",
    id: "support_email",
    description:
      "This email address will be given to the ticket buyers for support requests",
  },
  {
    name: "Language of the email communication",
    id: "email_language",
  },
  {
    name: "Company address",
    id: "company_address",
    description:
      "This address will be shown in the ticket purchase confirmation emails",
  },
  {
    name: "Company country",
    id: "company_country",
    description:
      "The country will be shown in the ticket purchase confirmation emails",
  },
  {
    name: "Company city",
    id: "company_city",
    description:
      "The city will be shown in the ticket purchase confirmation emails",
  },
  {
    name: "Website",
    id: "website",
  },
  {
    name: "Phone number",
    id: "phone_number",
  },
];

export const Profile = ({ userData, handleSnackbar, supabase }) => {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const [logo, setLogo] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleSelectLogo = (event) => {
    setLogo(event.target.files[0]);
  };

  const formik = useFormik({
    initialValues: {
      logo: userData?.logo || "",
      email: userData?.email || "",
      company_name: userData?.company_name || "",
      website: userData?.website || "",
      contact_person: userData?.contact_person || "",
      phone_number: userData?.phone_number || "",
      company_address: userData?.company_address || "",
      brand_color: userData?.main_color || "",
      company_country: userData?.company_country || "",
      company_city: userData?.company_city || "",
      event_type: userData?.event_type || "",
      support_email: userData?.support_email || "",
      email_language: userData?.email_language,
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      handleFormSubmit(values);
    },
  });

  const copyToClipboard = async (text) => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 2000);
  };

  const uploadImage = async (incomingFiles) => {
    try {
      setLoading(true);

      const { data, error } = await supabase.storage
        .from("ooval-bucket")
        .upload(
          `organization_folders/${
            userData?.email.split("@")[1].split(".")[0]
          }/${incomingFiles.name}`,
          incomingFiles,
          {
            upsert: true,
          }
        );

      if (error) {
        console.log(error);
        handleSnackbar(error.message, "error");
        setLoading(false);
        return;
      }

      const fileUrl = await getPublicUrl(data.path);

      setLoading(false);

      return fileUrl;
    } catch (err) {
      console.log(err);
      handleSnackbar(err.message, "error");
      setLoading(false);
    }
  };

  const getPublicUrl = async (path) => {
    const { data, error } = supabase.storage
      .from("ooval-bucket")
      .getPublicUrl(`${path}`);

    if (error) {
      console.log(error);
      return;
    }

    return data.publicUrl;
  };

  const handleFormSubmit = async (values) => {
    setLoading(true);
    try {
      if (logo) {
        const logoUrl = await uploadImage(logo);
        values["logo"] = logoUrl;
      }

      const response = await updateOrg(values, userData?._id);

      if (response.status === 200) {
        setLoading(false);

        dispatch(updateUser(response.data));
        handleSnackbar("Organization updated successfully!", "success");
      } else {
        setLoading(false);
        handleSnackbar(
          "Something went wrong, please try again later [1]",
          "error"
        );
      }
    } catch (error) {
      console.log("error: ", error);
      setLoading(false);
      handleSnackbar(
        "Something went wrong, please try again later [2]",
        "error"
      );
    }
  };

  return (
    <div>
      <div style={{ height: "20px" }} />
      <form className="standard-container">
        <h2>Organization Profile</h2>
        <p id="subTitle" style={{ marginBottom: "20px" }}>
          Change any information about your organization here.
        </p>
        <Chip
          sx={{
            width: "fit-content",
            background: COLORS.evenLighterGrey,
            border: `1px solid ${COLORS.lightGrey}`,
            marginBottom: "20px",
          }}
          label={`Organization ID: ${userData?._id?.slice(
            0,
            4
          )}...${userData?._id?.slice(21)}`}
          icon={
            copied ? (
              <BsCheckCircleFill color={COLORS.mainAccent} size={16} />
            ) : (
              <MdOutlineFileCopy size={16} />
            )
          }
          onClick={() => copyToClipboard(userData?._id)}
        />
        <div className="container-column">
          <p
            id="subheaderBlack"
            style={{ margin: "0px 0px 3px", alignSelf: "" }}
          >
            Logo
          </p>
          <div className="container-row">
            <img
              src={formik.values.logo}
              style={{
                height: "50px",
                width: "50px",
                objectFit: "cover",
                marginBottom: "10px",
              }}
            />
            <input
              type="file"
              className="input-upload"
              onChange={handleSelectLogo}
              placeholder="Upload logo"
            />
          </div>
        </div>
        <div className="field-container">
          <p
            id="subheaderBlack"
            style={{ margin: "0px 0px 3px", alignSelf: "center" }}
          >
            Email
          </p>
          <TextField
            fullWidth
            disabled
            size="small"
            className="input"
            id={"email"}
            placeholder="not set"
            value={formik.values.email}
            onChange={formik.handleChange}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
            sx={{
              "& label": { paddingLeft: (theme) => theme.spacing(1) },
              "& fieldset": {
                borderRadius: "6px",
                border: `2px solid #f0f1f5`,
                transition: ".2s ease-in-out all",
              },
              "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                {
                  borderColor: "#f0f1f5",
                },
              "& .MuiOutlinedInput-input:hover": {
                cursor: "not-allowed",
              },
            }}
          />
        </div>
        <div style={{ width: "-webkit-fill-available" }}>
          {fields1.map((field, index) => (
            <div className="field-container" key={index}>
              <p
                id="subheaderBlack"
                style={{ margin: "0px 0px 10px", alignSelf: "center" }}
              >
                {field.name}
              </p>
              {field.id !== "email_language" && (
                <TextField
                  fullWidth
                  disabled={loading}
                  size="small"
                  className="input"
                  id={field.id}
                  placeholder="not set"
                  value={formik.values[field.id]}
                  onChange={formik.handleChange}
                  error={
                    formik.touched[field.id] && Boolean(formik.errors[field.id])
                  }
                  helperText={
                    formik.touched[field.id] && formik.errors[field.id]
                  }
                  sx={{
                    "& label": { paddingLeft: (theme) => theme.spacing(1) },
                    "& fieldset": {
                      borderRadius: "6px",
                      border: `2px solid #f0f1f5`,
                      transition: ".2s ease-in-out all",
                    },
                    "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                      {
                        borderColor: "#f0f1f5",
                      },
                    "& .MuiOutlinedInput-input:hover": {
                      cursor: loading && "not-allowed",
                    },
                  }}
                />
              )}
              {field.id === "email_language" && (
                <Select
                  size="small"
                  name="email_language"
                  value={formik.values[field.id]}
                  onChange={formik.handleChange}
                  input={<OutlinedInput label="Name" />}
                  MenuProps={MenuProps}
                  style={{
                    maxWidth: "100px",
                    minWidth: "-webkit-fill-available",
                  }}
                  sx={{
                    "& label": {
                      paddingLeft: (theme) => theme.spacing(1),
                    },
                    "& fieldset": {
                      borderRadius: "6px",
                      border: `2px solid #f0f1f5`,
                      transition: ".2s ease-in-out all",
                    },
                    "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                      {
                        borderColor: "#f0f1f5",
                      },
                    "& .MuiOutlinedInput-input:hover": {
                      cursor: loading && "not-allowed",
                    },
                  }}
                >
                  <MenuItem value={"en"}>English</MenuItem>
                  <MenuItem value={"de"}>German</MenuItem>
                  <MenuItem value={"fr"}>French</MenuItem>
                </Select>
              )}
              {field.description && (
                <p id="subheader" style={{ margin: "10px 0 0" }}>
                  {field.description}
                </p>
              )}
            </div>
          ))}
        </div>
        <MainButton
          disabled={loading}
          text={"Update profile"}
          color={COLORS.black}
          shadowColor={"transparent"}
          textColor={COLORS.white}
          textHoverColor={COLORS.grey2}
          hoverColor={COLORS.black}
          textAlignment={"left"}
          onClickAction={() => formik.handleSubmit()}
          fontSize={"16px"}
          width={"fit-content"}
        >
          {loading ? (
            <HashLoader size={20} color={COLORS.white} />
          ) : (
            <AiOutlineSave size={20} />
          )}
        </MainButton>
      </form>
    </div>
  );
};
